/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action } from '@ember/object';
import { filter, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal imports
import EditAssessment from 'linced-admin/mixins/edit-assessment';
// External Imports
import AssessmentType from '@linc-technologies/linced-common/enums/assessment-type';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
export default class AssessmentController extends Controller.extend(
  EditAssessment,
) {
  // Services
  @service
  session;

  @service
  store;

  @service
  toaster;

  AssessmentType = AssessmentType;

  @alias('model.assessment')
  assessment;

  @alias('model.modules')
  modules;

  @tracked
  editingModule = null;

  @tracked
  moduleLabel = '';

  @action
  setModuleLabel(value) {
    this.moduleLabel = value;
  }

  @tracked
  moduleDescription = '';

  @action
  setModuleDescription(value) {
    this.moduleDescription = value;
  }

  @tracked
  moduleIsModerated = false;

  @action
  setModuleIsModerated(value) {
    this.moduleIsModerated = value;
  }

  @tracked
  moduleSharesEvidence = false;

  @action
  setModuleSharesEvidence(value) {
    this.moduleSharesEvidence = value;
  }

  @tracked
  selectedGradeSchedule = null;

  @action
  selectGradeSchedule(schedule) {
    this.selectedGradeSchedule = schedule;
  }

  @tracked
  deleteDialogOpen = false;

  @tracked
  entityToDelete = null;

  @tracked
  deleteErrorMessage = '';

  @tracked
  selectedTab;

  @action
  setSelectedTab(value) {
    this.selectedTab = value;
  }

  // availableSchedules is an array of schedules which are available to the current user.
  @filter(
    'model.gradeschedules',
    ['model.gradeschedules.[]', 'editingModule.id'],
    function (schedule) {
      let personId = this.get('session.personId');
      let selectedId = this.get('selectedGradeSchedule.id');

      return (
        schedule.get('isShared') ||
        schedule.get('authorId') === personId ||
        schedule.get('id') === selectedId
      );
    },
  )
  availableSchedules;

  @tracked
  deleteInProgress;

  @tracked
  moduleDialogOpen;

  @tracked
  moduleErrorMessage;

  @tracked
  moduleSaveInProgress;

  @action
  saveModule() {
    let module = this.editingModule;
    let store = this.store;
    let isNew = false;
    this.moduleSaveInProgress = true;

    // Create a new Module if needed
    if (isEmpty(module)) {
      isNew = true;
      module = store.createRecord('assessments/module', {
        schoolId: this.get('session.school'),
        assessmentId: this.assessment,
        authorId: this.get('session.person'),
        // Add to the end by default
        sequence: this.get('assessment.modules.length'),
      });
    }

    // Set properties from form
    module.setProperties({
      label: this.moduleLabel,
      description: this.moduleDescription,
      isModerated: this.moduleIsModerated,
      sharesEvidence: this.moduleSharesEvidence,
      gradeScheduleId: this.selectedGradeSchedule,
    });

    module
      .save()
      .then(() => {
        this.moduleDialogOpen = false;
        if (isNew) {
          // Reload the list of modules so that the new one appears.
          this.modules.update();
        }
      })
      .catch((err) => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(module);
        this.moduleErrorMessage = err;
      })
      .then(() => {
        this.moduleSaveInProgress = false;
      });
  }

  @action
  toggleModuleDialog() {
    this.toggleProperty('moduleDialogOpen');
  }

  @action
  async newModule() {
    await this.resetModuleForm();
    this.moduleDialogOpen = true;
  }

  @action
  async editModule(module) {
    await this.resetModuleForm();
    this.editingModule = module;
    this.moduleLabel = module.get('label');
    this.moduleDescription = module.get('description');
    this.moduleIsModerated = module.get('isModerated');
    this.moduleSharesEvidence = module.get('sharesEvidence');
    // A belongsTo takes a record, not the async proxy.
    this.selectedGradeSchedule = await module.gradeSchedule;
    this.moduleDialogOpen = true;
  }

  @action
  moveModule({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      // No change in list or position.
      return;
    }

    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    // Update assessment's module sequences.
    targetList.forEach(function (item, index) {
      item.set('sequence', index);
      if (item.get('hasDirtyAttributes')) {
        item.save();
      }
    });
  }

  @action
  deleteModule(module) {
    this.deleteDialogOpen = true;
    this.entityToDelete = module;
  }

  @action
  confirmDelete(confirmed) {
    if (!confirmed) {
      // reset the deletion values
      this.deleteDialogOpen = false;
      this.entityToDelete = null;
      this.deleteErrorMessage = '';

      // we don't want to delete anything 👋
      return;
    }

    return new Promise((resolve, reject) => {
      // Deletion confirmed; actually delete the record.
      this.deleteInProgress = true;
      let entity = this.get('entityToDelete');
      // Delete record (locally + pushed to server).
      return entity
        .destroyRecord()
        .then(resolve)
        .catch((err) => {
          // Remove 'deleted' flag so that entity can be interacted with again.
          entity.rollbackAttributes();
          reject(err);
        });
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteDialogOpen = false;
        this.entityToDelete = null;
        this.deleteErrorMessage = '';
        this.toaster.success(`Assessment module deleted successfully.`);
      })
      .catch((err) => {
        if (!isEmpty(err)) {
          if (err.errors && err.errors.length > 0) {
            this.deleteErrorMessage = err.errors[0].detail;
            return;
          }

          if (err.message) {
            this.deleteErrorMessage = err.message;
            return;
          }
        }

        this.deleteErrorMessage =
          'An unknown error occurred. Please try again.';
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  async resetModuleForm() {
    this.moduleErrorMessage = '';
    this.moduleLabel = '';
    this.moduleDescription = '';
    this.moduleIsModerated = this.get('assessment.isModerated');
    this.moduleSharesEvidence = this.get('assessment.sharesEvidence');
    // A belongsTo takes a record, not the async proxy.
    this.selectedGradeSchedule = await this.assessment.gradeSchedule;
    this.editingModule = null;
  }
}
